import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const RegisterItAssetLayer = () => {
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [picture, setPicture] = useState(null);
    const [picturePreview, setPicturePreview] = useState('');

    const [formData, setFormData] = useState({
        asset_id: '',
        brand_model: '',
        date_acquired: '',
        department: '',
        category: '',
        location: '',
        condition: 'good',
        description: '',
        assigned_user_department: '',
        asset_owner: '',
        remarks: '',
        serial_number: '',
        supplier_vendor: '',
    });

    const handlePictureChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPicture(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPicturePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear validation error for this field
        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setValidationErrors({});

        const apiUrl = process.env.REACT_APP_API_URL;
        const token = localStorage.getItem('authToken');

        if (!apiUrl || !token) {
            setError('Missing API URL or authentication token');
            return;
        }

        try {
            setIsSubmitting(true);

            // Prepare form data for multipart/form-data submission (for picture upload)
            const formDataToSend = new FormData();
            
            // Append all form fields
            Object.keys(formData).forEach(key => {
                if (formData[key]) {
                    formDataToSend.append(key, formData[key]);
                }
            });

            // Append picture if exists
            if (picture) {
                formDataToSend.append('picture', picture);
            }

            const response = await fetch(`${apiUrl}/it-assets`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    // Don't set Content-Type header - browser will set it automatically with boundary
                },
                body: formDataToSend,
            });

            const data = await response.json();

            if (!response.ok) {
                if (data.errors) {
                    setValidationErrors(data.errors);
                }
                throw new Error(data.message || 'Failed to register IT asset');
            }

            if (data.success) {
                navigate('/it-asset-list');
            } else {
                throw new Error(data.message || 'Failed to register IT asset');
            }
        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : 'Unexpected error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate('/it-asset-list');
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-body p-24">
                <div className="row justify-content-center">
                    <div className="col-12">
                        <div className="card border">
                            <div className="card-body">
                                <h5 className="mb-24">Add New Asset</h5>
                              
                                {error && (
                                    <div className="alert alert-danger mb-20" role="alert">
                                        {error}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} noValidate>
                                    <div className="row">
                                        {/* Row 1: Asset ID / Tag Number | Category / Type */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="asset_id"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Asset ID / Tag Number <span className="text-danger-600">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control radius-8 ${
                                                    validationErrors.asset_id ? 'is-invalid' : ''
                                                }`}
                                                id="asset_id"
                                                name="asset_id"
                                                placeholder="Enter tag number"
                                                value={formData.asset_id}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            {validationErrors.asset_id && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.asset_id[0]}
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="category"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Category / Type <span className="text-danger-600">*</span>
                                            </label>
                                            <select
                                                className={`form-control radius-8 form-select ${
                                                    validationErrors.category ? 'is-invalid' : ''
                                                }`}
                                                id="category"
                                                name="category"
                                                value={formData.category}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select Category</option>
                                                <option value="Computing Devices">Computing Devices</option>
                                                <option value="Peripherals & Accessories">Peripherals & Accessories</option>
                                                <option value="Networking & Power Equipment">Networking & Power Equipment</option>
                                                <option value="Software & Licenses">Software & Licenses</option>
                                            </select>
                                            {validationErrors.category && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.category[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Row 2: Brand / Model | Serial Number */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="brand_model"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Brand / Model <span className="text-danger-600">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control radius-8 ${
                                                    validationErrors.brand_model ? 'is-invalid' : ''
                                                }`}
                                                id="brand_model"
                                                name="brand_model"
                                                placeholder="Enter brand / model"
                                                value={formData.brand_model}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            {validationErrors.brand_model && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.brand_model[0]}
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="serial_number"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Serial Number
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control radius-8 ${
                                                    validationErrors.serial_number ? 'is-invalid' : ''
                                                }`}
                                                id="serial_number"
                                                name="serial_number"
                                                placeholder="Enter serial number"
                                                value={formData.serial_number}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.serial_number && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.serial_number[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Row 3: Specifications / Description (full width) */}
                                        <div className="col-12 mb-20">
                                            <label
                                                htmlFor="description"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Specifications / Description
                                            </label>
                                            <textarea
                                                className={`form-control radius-8 ${
                                                    validationErrors.description ? 'is-invalid' : ''
                                                }`}
                                                id="description"
                                                name="description"
                                                placeholder="Enter description"
                                                rows="3"
                                                value={formData.description}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.description && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.description[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Row 4: Assigned User / Department | Location */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="assigned_user_department"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Assigned User / Department
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control radius-8"
                                                id="assigned_user_department"
                                                name="assigned_user_department"
                                                placeholder="Enter assigned user / department"
                                                value={formData.assigned_user_department}
                                                onChange={handleInputChange}
                                            />
                                        </div>

                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="location"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Location <span className="text-danger-600">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control radius-8 ${
                                                    validationErrors.location ? 'is-invalid' : ''
                                                }`}
                                                id="location"
                                                name="location"
                                                placeholder="Enter location"
                                                value={formData.location}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            {validationErrors.location && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.location[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Row 5: Date Acquired / Purchase Date | Supplier / Vendor */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="date_acquired"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Date Acquired / Purchase Date <span className="text-danger-600">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                className={`form-control radius-8 ${
                                                    validationErrors.date_acquired ? 'is-invalid' : ''
                                                }`}
                                                id="date_acquired"
                                                name="date_acquired"
                                                value={formData.date_acquired}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            {validationErrors.date_acquired && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.date_acquired[0]}
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="supplier_vendor"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Supplier / Vendor
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control radius-8"
                                                id="supplier_vendor"
                                                name="supplier_vendor"
                                                placeholder="Enter supplier / vendor"
                                                value={formData.supplier_vendor}
                                                onChange={handleInputChange}
                                            />
                                        </div>

                                        {/* Row 6: Department | Condition / Status */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="department"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Department <span className="text-danger-600">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control radius-8 ${
                                                    validationErrors.department ? 'is-invalid' : ''
                                                }`}
                                                id="department"
                                                name="department"
                                                placeholder="Enter department"
                                                value={formData.department}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            {validationErrors.department && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.department[0]}
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="condition"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Condition / Status <span className="text-danger-600">*</span>
                                            </label>
                                            <select
                                                className={`form-control radius-8 form-select ${
                                                    validationErrors.condition ? 'is-invalid' : ''
                                                }`}
                                                id="condition"
                                                name="condition"
                                                value={formData.condition}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="excellent">Excellent</option>
                                                <option value="good">Good</option>
                                                <option value="fair">Fair</option>
                                                <option value="poor">Poor</option>
                                                <option value="damaged">Damaged</option>
                                            </select>
                                            {validationErrors.condition && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.condition[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Row 7: Asset Owner (Custodian) | Picture */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="asset_owner"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Asset Owner (Custodian)
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control radius-8"
                                                id="asset_owner"
                                                name="asset_owner"
                                                placeholder="Enter asset owner / custodian"
                                                value={formData.asset_owner}
                                                onChange={handleInputChange}
                                            />
                                        </div>

                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="picture"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Picture
                                            </label>
                                            <input
                                                type="file"
                                                className="form-control radius-8"
                                                id="picture"
                                                accept="image/*"
                                                onChange={handlePictureChange}
                                            />
                                            {picturePreview && (
                                                <div className="mt-2">
                                                    <img
                                                        src={picturePreview}
                                                        alt="Preview"
                                                        className="img-thumbnail"
                                                        style={{ maxWidth: '200px', maxHeight: '200px' }}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {/* Row 8: Remarks / Notes (full width) */}
                                        <div className="col-12 mb-20">
                                            <label
                                                htmlFor="remarks"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Remarks / Notes
                                            </label>
                                            <textarea
                                                className="form-control radius-8"
                                                id="remarks"
                                                name="remarks"
                                                placeholder="Enter any remarks or notes"
                                                rows="3"
                                                value={formData.remarks}
                                                onChange={handleInputChange}
                                            />
                                        </div>

                                    </div>

                                    <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                                        <button
                                            type="button"
                                            onClick={handleCancel}
                                            className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                                            disabled={isSubmitting}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? 'Saving...' : 'Save Asset'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegisterItAssetLayer;
